import { useEffect, useState } from 'react';
import { Button } from './button';
import { tr } from '@/i18n';

// Diálogo de confirmación/aviso CON ESTILO de la app, invocable de forma imperativa como promesa:
//
//   if (await confirmDialog('¿Borrar esto?')) { ... }
//   if (await confirmDialog({ message: '…', confirmText: 'Eliminar', destructive: true })) { ... }
//   await alertDialog('No se pudo generar el informe.');
//
// Sustituye a window.confirm()/window.alert() (modales sin estilo del navegador). Requiere montar
// <ConfirmHost /> una sola vez en la raíz (ver app.tsx). Si el host no estuviera montado, recae en
// el confirm/alert nativo para no perder la interacción.

export interface ConfirmOptions {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    /** Botón de confirmar en rojo (acciones de borrado/irreversibles). */
    destructive?: boolean;
    /** Solo aviso: oculta «Cancelar» y resuelve siempre true al cerrar. */
    alert?: boolean;
}

interface Request extends ConfirmOptions {
    resolve: (value: boolean) => void;
}

let notify: ((req: Request | null) => void) | null = null;

export function confirmDialog(opts: string | ConfirmOptions): Promise<boolean> {
    const options: ConfirmOptions = typeof opts === 'string' ? { message: opts } : opts;
    return new Promise((resolve) => {
        if (!notify) {
            // Host no montado: degradación elegante al diálogo nativo.
            resolve(options.alert ? (window.alert(options.message), true) : window.confirm(options.message));
            return;
        }
        notify({ ...options, resolve });
    });
}

/** Aviso de un solo botón (equivalente con estilo a window.alert). */
export function alertDialog(message: string, title?: string): Promise<boolean> {
    return confirmDialog({ message, title, alert: true, confirmText: tr('comun.acciones.entendido') });
}

export function ConfirmHost() {
    const [req, setReq] = useState<Request | null>(null);

    const close = (value: boolean) => {
        if (!req) return;
        req.resolve(req.alert ? true : value);
        setReq(null);
    };

    useEffect(() => {
        notify = setReq;
        return () => { notify = null; };
    }, []);

    useEffect(() => {
        if (!req) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close(false);
            else if (e.key === 'Enter') close(true);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [req]);

    if (!req) return null;

    return (
        <div
            className="fixed inset-0 z-confirm flex items-center justify-center bg-black/60 p-4"
            onClick={() => close(false)}
        >
            <div
                role="dialog"
                aria-modal="true"
                className="bg-card text-foreground rounded-lg shadow-xl w-full max-w-md border border-border"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-5 space-y-1.5">
                    <h2 className="text-base font-semibold">{req.title ?? tr(req.alert ? 'comun.dialogo.aviso' : 'comun.dialogo.confirmar')}</h2>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">{req.message}</p>
                </div>
                <div className="flex justify-end gap-2 px-5 pb-5">
                    {!req.alert && (
                        <Button variant="outline" size="sm" onClick={() => close(false)}>
                            {req.cancelText ?? tr('comun.acciones.cancelar')}
                        </Button>
                    )}
                    <Button
                        variant={req.destructive ? 'destructive' : 'default'}
                        size="sm"
                        autoFocus
                        onClick={() => close(true)}
                    >
                        {req.confirmText ?? tr('comun.acciones.confirmar')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
